import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { AdminBreadcrumb } from '../AdminBreadcrumb';
import { ProfileSection } from './ProfileSection';
import { ThemeSection } from './ThemeSection';
import { AccountSection } from './AccountSection';
import { SupportSection } from './SupportSection';
import { OrgSettingsSection } from './OrgSettingsSection';
import { MembersSection } from './MembersSection';
import { ApiAccessSection } from './ApiAccessSection';
import { CalendarsSection } from './CalendarsSection';
import { StorageSection } from './StorageSection';
import { useReleaseFeature } from '@/lib/release-flags';

/** Navigation items for the settings sidebar. */
const NAV_ITEMS = [
  { id: 'profile', label: 'Profile' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'organization', label: 'Organization' },
  { id: 'members', label: 'Members' },
  { id: 'account', label: 'Account' },
  { id: 'api', label: 'API access' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'support', label: 'Help & Support' },
] as const;

/**
 * Settings page with sticky left nav and scrollable content sections.
 * Uses IntersectionObserver to highlight the active section in the sidebar.
 */
export const SettingsPage: React.FC = () => {
  const calendarBookingsEnabled = useReleaseFeature('calendarBookings');
  const fileStorageEnabled = useReleaseFeature('fileStorage');
  // Integrations appears only once at least one integration is released.
  const integrationsEnabled = calendarBookingsEnabled || fileStorageEnabled;
  const navItems = useMemo(
    () => NAV_ITEMS.filter(({ id }) => id !== 'integrations' || integrationsEnabled),
    [integrationsEnabled],
  );
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('profile');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  /**
   * Ref set when the user clicks a nav item directly. While set, the
   * IntersectionObserver is suppressed so it cannot override the selection
   * mid-scroll (the observer fires during the smooth-scroll animation and
   * would otherwise land on whichever section is briefly most visible).
   */
  const manualScrollTarget = useRef<string | null>(null);
  const manualScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Auto-scroll to a section on:
   *   - ?section=<id> search param (generic deep-link)
   *   - ?report=<type> search param (e.g. from "Report Bug" — scrolls to support)
   *   - location.state.scrollTo (set by sidebar nav items; always a fresh
   *     reference so the effect fires even when the URL is already identical)
   */
  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    const sectionParam = searchParams.get('section');
    const reportParam = searchParams.get('report');
    const requestedSection = state?.scrollTo ?? sectionParam ?? (reportParam ? 'support' : null);
    const targetId = requestedSection === 'calendars' || requestedSection === 'storage' ? 'integrations' : requestedSection;
    if (!targetId) return undefined;

    const timer = setTimeout(() => {
      sectionRefs.current[targetId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [searchParams, location.state]);

  useEffect(() => {
    // Find the nearest scrollable ancestor (AdminLayout's <main>)
    let scrollParent: HTMLElement | null = null;
    let ancestor = contentRef.current?.parentElement ?? null;
    while (ancestor) {
      const { overflowY } = getComputedStyle(ancestor);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParent = ancestor;
        break;
      }
      ancestor = ancestor.parentElement;
    }
    if (!scrollParent) return undefined;

    // Track intersection ratios for ALL sections (not just changed entries)
    const ratios: Record<string, number> = {};

    const updateActiveSection = () => {
      // While the user just clicked a nav item, suppress observer updates so
      // the selection is not overridden mid-scroll by whatever section happens
      // to be momentarily most visible during the animation.
      if (manualScrollTarget.current) return;
      // Check if scrolled to bottom of the scroll container
      const atBottom = scrollParent!.scrollTop + scrollParent!.clientHeight >= scrollParent!.scrollHeight - 50;
      if (atBottom) {
        setActiveSection(navItems[navItems.length - 1].id);
        return;
      }
      // Find the section with the highest intersection ratio
      let bestId = '';
      let bestRatio = 0;
      for (const { id } of navItems) {
        const ratio = ratios[id] ?? 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = id;
        }
      }
      if (bestId) {
        setActiveSection(bestId);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          ratios[e.target.id] = e.isIntersecting ? e.intersectionRatio : 0;
        });
        updateActiveSection();
      },
      { root: scrollParent, rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.25, 0.5] }
    );

    // Listen for scroll events to catch atBottom state changes
    scrollParent.addEventListener('scroll', updateActiveSection, { passive: true });

    navItems.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      scrollParent!.removeEventListener('scroll', updateActiveSection);
    };
  }, [navItems]);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    manualScrollTarget.current = id;
    if (manualScrollTimer.current) clearTimeout(manualScrollTimer.current);
    // Release the lock after the smooth-scroll animation completes (~800ms).
    manualScrollTimer.current = setTimeout(() => { manualScrollTarget.current = null; }, 800);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="pt-16 md:pt-0">
      {/* Sticky header bar */}
      <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4 min-h-[36px]">
            <h1 className="text-xl font-bold text-foreground truncate">Settings</h1>
          </div>
          <AdminBreadcrumb items={[
            { label: 'Conversations', href: '/admin' },
            { label: 'Settings' },
          ]} />
        </div>
      </div>

      {/* Two-column layout */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-12">
          {/* Left sidebar nav - hidden on mobile */}
          <nav className="hidden md:block w-48 shrink-0">
            <div className="sticky top-24 space-y-1">
              {navItems.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                    activeSection === id
                      ? 'bg-surface text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </nav>

          {/* Scrollable content area */}
          <div ref={contentRef} className="flex-1 min-w-0 max-w-2xl space-y-12">
            <div id="profile" className="scroll-mt-24" ref={(el) => { sectionRefs.current.profile = el; }}>
              <ProfileSection />
            </div>
            <div id="appearance" className="scroll-mt-24" ref={(el) => { sectionRefs.current.appearance = el; }}>
              <ThemeSection />
            </div>
            <div id="organization" className="scroll-mt-24" ref={(el) => { sectionRefs.current.organization = el; }}>
              <OrgSettingsSection />
            </div>
            <div id="members" className="scroll-mt-24" ref={(el) => { sectionRefs.current.members = el; }}>
              <MembersSection />
            </div>
            <div id="account" className="scroll-mt-24" ref={(el) => { sectionRefs.current.account = el; }}>
              <AccountSection />
            </div>
            <div id="api" className="scroll-mt-24" ref={(el) => { sectionRefs.current.api = el; }}>
              <ApiAccessSection />
            </div>
            {integrationsEnabled ? (
              <section id="integrations" className="scroll-mt-24" ref={(el) => { sectionRefs.current.integrations = el; }}>
                <h2 className="mb-6 text-lg font-semibold text-foreground">Integrations</h2>
                <div className="space-y-2">
                  {calendarBookingsEnabled ? <CalendarsSection /> : null}
                  {fileStorageEnabled ? <StorageSection /> : null}
                </div>
              </section>
            ) : null}
            <div id="support" className="scroll-mt-24" ref={(el) => { sectionRefs.current.support = el; }}>
              <SupportSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
